import {useMemo} from 'react';

import {
  KVLoadEvent,
  useKVCacheManager,
} from '../../../../../../../../common/state/KVCacheManager';
import {useGetTraceServerClientContext} from '../../../wfReactInterface/traceServerClientContext';
import {TraceTableQueryReq} from '../../../wfReactInterface/traceServerClientTypes';

/**
 * Input Caching Manager
 *
 * High-level purpose
 * - Provides a small adapter around a generic KV cache to lazily fetch and cache
 *   dataset row inputs from the Trace Server as cells come into view.
 * - Exposes a minimal API tailored for table-like UIs:
 *   - eventObserver.subscribe(listener): consumers subscribe to batched load
 *     events for individual row digests.
 *   - requestRowData(digest): enqueue a load request for a row's input data.
 *   - purgeCache(): clear all cached data.
 *
 * Behavior and design
 * - Batching: requests are coalesced (batchSize, debounceMs) to reduce server requests.
 * - Loading: uses tableQuery with row_digests filter to fetch multiple rows at once.
 * - Events: normalizes the underlying KV events to InputLoadEvent so callers
 *   can render loading spinners, values, or errors.
 * - Eviction: purgeCache delegates to the KV cache eviction, allowing upstream
 *   cache policy (e.g., LRU) to reclaim memory when needed.
 *
 * Typical usage
 * - A cell component subscribes once in an effect, calls requestRowData when
 *   it mounts or becomes visible, renders based on event status/value, and
 *   the cache manager handles batching and deduplication automatically.
 */

export type InputLoadEvent = {
  digest: string;
  status: 'loading' | 'loaded' | 'error' | 'evicted';
  data?: any;
  error?: any;
};

export type InputEventObserver = {
  subscribe: (listener: (evt: InputLoadEvent) => void) => () => void;
};

export type InputCachingManager = {
  eventObserver: InputEventObserver;
  requestRowData: (digest: string) => void;
  purgeCache: () => void;
};

type PartialTableRequestType = Pick<
  TraceTableQueryReq,
  'project_id' | 'digest'
>;

type RowData = {digest: string; val: any};

export const useInputCachingManager = (
  partialTableRequest: PartialTableRequestType | null
): InputCachingManager => {
  const getTsClient = useGetTraceServerClientContext();

  const kvOptions = useMemo(
    () => ({
      batchLoad: async (digests: readonly string[]) => {
        if (partialTableRequest == null) {
          return [];
        }
        const rowsRes = await getTsClient().tableQuery({
          ...partialTableRequest,
          filter: {
            row_digests: digests as string[],
          },
        });
        return rowsRes.rows.map(row => ({
          digest: row.digest,
          val: row.val,
        }));
      },
      getId: (row: RowData) => row.digest,
      getValue: (row: RowData) => row.val,
      batchSize: 500,
      debounceMs: 10,
    }),
    [getTsClient, partialTableRequest]
  );

  const kv = useKVCacheManager<string, RowData, any>(kvOptions);

  const eventObserver: InputEventObserver = useMemo(() => {
    return {
      subscribe: (listener: (evt: InputLoadEvent) => void) =>
        kv.eventObserver.subscribe((e: KVLoadEvent<string, any>) =>
          listener({
            digest: e.id,
            status: e.status === 'idle' ? 'loading' : e.status,
            data: e.value,
            error: e.error,
          })
        ),
    };
  }, [kv.eventObserver]);

  return useMemo(
    () => ({
      eventObserver,
      requestRowData: (digest: string) => kv.request(digest),
      purgeCache: () => kv.purgeCache(),
    }),
    [eventObserver, kv]
  );
};
